import React from 'react';
import { GitBranch, Maximize2, Minimize2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui';
import {
  WorkflowFlowDiagram,
  type FlowDirection,
  type DiagramStep,
} from '../../components/workflow/WorkflowFlowDiagram.js';
import type { WorkflowEdgeForm } from './WorkflowBuilderTypes.js';

interface ExecutionFlowPanelProps {
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onExpandToggle: () => void;
  diagramSteps: DiagramStep[];
  selectedStepId: string | null;
  onStepClick: (stepId: string) => void;
  onOutputClick?: (stepId: string) => void;
  flowDirection: FlowDirection;
  onDirectionToggle: () => void;
  onPositionsChange: (positions: Record<string, { x: number; y: number }>) => void;
  // ── Canvas editing (optional — absent = read-only) ──
  editable?: boolean;
  formEdges?: WorkflowEdgeForm[];
  onAddEdge?: (sourceStepId: string, targetStepId: string) => void;
  onRemoveStep?: (stepId: string) => void;
  onRemoveEdge?: (sourceStepId: string, targetStepId: string) => void;
  onDropStep?: (kind: string, position: { x: number; y: number }) => void;
  onUpdateStep?: (stepId: string, updates: { name: string }) => void;
}

interface ExecutionFlowContentProps {
  diagramSteps: DiagramStep[];
  selectedStepId: string | null;
  onStepClick: (stepId: string) => void;
  onOutputClick?: (stepId: string) => void;
  flowDirection: FlowDirection;
  onDirectionToggle: () => void;
  onPositionsChange: (positions: Record<string, { x: number; y: number }>) => void;
  editable?: boolean;
  formEdges?: WorkflowEdgeForm[];
  onAddEdge?: (sourceStepId: string, targetStepId: string) => void;
  onRemoveStep?: (stepId: string) => void;
  onRemoveEdge?: (sourceStepId: string, targetStepId: string) => void;
  onDropStep?: (kind: string, position: { x: number; y: number }) => void;
  onUpdateStep?: (stepId: string, updates: { name: string }) => void;
}

export const ExecutionFlowContent: React.FC<ExecutionFlowContentProps> = ({
  diagramSteps,
  selectedStepId,
  onStepClick,
  onOutputClick,
  flowDirection,
  onDirectionToggle,
  onPositionsChange,
  editable,
  formEdges,
  onAddEdge,
  onRemoveStep,
  onRemoveEdge,
  onDropStep,
  onUpdateStep,
}) => (
  <div className="flex-1 min-h-0 p-2 pt-0">
    <WorkflowFlowDiagram
      steps={diagramSteps}
      selectedStepId={selectedStepId}
      onStepClick={onStepClick}
      onOutputClick={onOutputClick}
      direction={flowDirection}
      onDirectionToggle={onDirectionToggle}
      onPositionsChange={onPositionsChange}
      editable={editable}
      formEdges={formEdges}
      onAddEdge={onAddEdge}
      onRemoveStep={onRemoveStep}
      onRemoveEdge={onRemoveEdge}
      onDropStep={onDropStep}
      onUpdateStep={onUpdateStep}
    />
  </div>
);

export const ExecutionFlowPanel: React.FC<ExecutionFlowPanelProps> = ({
  collapsed,
  expanded,
  onToggle,
  onExpandToggle,
  diagramSteps,
  selectedStepId,
  onStepClick,
  onOutputClick,
  flowDirection,
  onDirectionToggle,
  onPositionsChange,
  editable,
  formEdges,
  onAddEdge,
  onRemoveStep,
  onRemoveEdge,
  onDropStep,
  onUpdateStep,
}) => (
  <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-surface">
    <div className="flex shrink-0 items-center justify-between px-4 py-2.5">
      <h3 className="flex items-center gap-2 font-semibold text-heading text-sm">
        <GitBranch size={14} /> Execution flow
      </h3>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onExpandToggle}
          aria-label={expanded ? 'Restore all sections' : 'Expand'}
          title={expanded ? 'Restore all sections' : 'Expand'}
        >
          {expanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onToggle}
          aria-label={collapsed ? 'Show execution flow' : 'Collapse execution flow'}
          title={collapsed ? 'Show' : 'Collapse'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </Button>
      </div>
    </div>
    {!collapsed && (
      <ExecutionFlowContent
        diagramSteps={diagramSteps}
        selectedStepId={selectedStepId}
        onStepClick={onStepClick}
        onOutputClick={onOutputClick}
        flowDirection={flowDirection}
        onDirectionToggle={onDirectionToggle}
        onPositionsChange={onPositionsChange}
        editable={editable}
        formEdges={formEdges}
        onAddEdge={onAddEdge}
        onRemoveStep={onRemoveStep}
        onRemoveEdge={onRemoveEdge}
        onDropStep={onDropStep}
        onUpdateStep={onUpdateStep}
      />
    )}
  </div>
);
